import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Mail, ArrowDownLeft, ArrowUpRight, Search, Filter,
  ChevronLeft, ChevronRight, ExternalLink, Clock,
  MessageSquare, RefreshCw, Inbox, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  event: 'Delay Event',
  notice: 'Notice',
  claim: 'Claim',
  rfi: 'RFI',
  si: 'Site Instruction',
  sd: 'Shop Drawing',
  vo: 'Variation Order',
  mm: 'Meeting Minutes',
};

const MODULE_COLORS = {
  event: 'bg-orange-100 text-orange-700 border-orange-200',
  notice: 'bg-red-100 text-red-700 border-red-200',
  claim: 'bg-purple-100 text-purple-700 border-purple-200',
  rfi: 'bg-blue-100 text-blue-700 border-blue-200',
  si: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sd: 'bg-amber-100 text-amber-700 border-amber-200',
  vo: 'bg-pink-100 text-pink-700 border-pink-200',
  mm: 'bg-cyan-100 text-cyan-700 border-cyan-200',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function getModuleUrl(moduleType, entityId, projectId) {
  const map = {
    event: `/projects/${projectId}/events/${entityId}`,
    notice: `/projects/${projectId}/notices/${entityId}`,
    claim: `/projects/${projectId}/claims/${entityId}`,
    rfi: `/projects/${projectId}/rfi/${entityId}`,
    si: `/projects/${projectId}/si/${entityId}`,
    sd: `/projects/${projectId}/drawings/${entityId}`,
    vo: `/projects/${projectId}/variation-orders/${entityId}`,
    mm: `/projects/${projectId}#meeting-minutes`,
  };
  return map[moduleType] || `/projects/${projectId}`;
}

export default function CorrespondenceDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    project_id: '',
    module_type: '',
    direction: '',
    search: '',
    page: 1,
  });
  const [searchInput, setSearchInput] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.module_type) params.append('module_type', filters.module_type);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page);
      params.append('limit', 30);

      const res = await fetch(`${API}/api/correspondence/dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error('Failed to load correspondence');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.project_id, filters.module_type, filters.direction, filters.search, filters.page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(f => ({ ...f, search: searchInput, page: 1 }));
  };

  const stats = data?.stats || { total_inbound: 0, total_outbound: 0, by_module: {} };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="correspondence-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase text-slate-900" data-testid="correspondence-title">
              Correspondence Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              All inbound email replies and correspondence across modules
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-correspondence-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="correspondence-stats">
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-4 w-4 text-slate-400" />
              <p className="text-xs font-bold uppercase text-slate-500">Total</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold" data-testid="stat-total">
              {(stats.total_inbound || 0) + (stats.total_outbound || 0)}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="h-4 w-4 text-blue-500" />
              <p className="text-xs font-bold uppercase text-blue-600">Inbound</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-blue-600" data-testid="stat-inbound">
              {stats.total_inbound || 0}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Send className="h-4 w-4 text-green-500" />
              <p className="text-xs font-bold uppercase text-green-600">Outbound</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-green-600" data-testid="stat-outbound">
              {stats.total_outbound || 0}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Filter className="h-4 w-4 text-slate-400" />
              <p className="text-xs font-bold uppercase text-slate-500">Modules</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold" data-testid="stat-modules">
              {Object.keys(stats.by_module || {}).length}
            </p>
          </div>
        </div>

        {/* Module breakdown chips */}
        {Object.keys(stats.by_module || {}).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="module-breakdown">
            {Object.entries(stats.by_module).map(([mod, counts]) => (
              <button
                key={mod}
                onClick={() => setFilters(f => ({
                  ...f,
                  module_type: f.module_type === mod ? '' : mod,
                  page: 1
                }))}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium border transition-all ${
                  filters.module_type === mod
                    ? 'ring-2 ring-slate-400 shadow-sm'
                    : 'hover:shadow-sm'
                } ${MODULE_COLORS[mod] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
                data-testid={`module-chip-${mod}`}
              >
                {MODULE_LABELS[mod] || mod}
                <span className="font-mono">{counts.inbound + counts.outbound}</span>
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search sender, subject, content..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9 rounded-sm h-9 text-sm"
                  data-testid="correspondence-search-input"
                />
              </div>
              <Button type="submit" size="sm" className="rounded-sm" data-testid="correspondence-search-btn">
                Search
              </Button>
            </form>
            <div className="flex gap-2">
              <Select
                value={filters.project_id || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, project_id: v === 'all' ? '' : v, page: 1 }))}
              >
                <SelectTrigger className="w-[180px] h-9 rounded-sm text-xs" data-testid="filter-project">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {(data?.projects || []).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.direction || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, direction: v === 'all' ? '' : v, page: 1 }))}
              >
                <SelectTrigger className="w-[130px] h-9 rounded-sm text-xs" data-testid="filter-direction">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="correspondence-list">
          {loading && !data ? (
            <div className="p-12 text-center text-slate-400">
              <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
              <p>Loading correspondence...</p>
            </div>
          ) : (data?.messages || []).length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Mail className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No correspondence found</p>
              <p className="text-sm mt-1">Email replies will appear here when consultants respond to system emails</p>
            </div>
          ) : (
            (data?.messages || []).map((msg) => (
              <div
                key={msg.id}
                className="p-3 sm:p-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                onClick={() => {
                  const entityId = msg.module_entity_id || msg.event_id;
                  if (entityId && msg.project_id) {
                    navigate(getModuleUrl(msg.module_type, entityId, msg.project_id));
                  }
                }}
                data-testid={`correspondence-row-${msg.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Direction Icon */}
                  <div className={`shrink-0 mt-0.5 rounded-full p-1.5 ${
                    msg.direction === 'inbound'
                      ? 'bg-blue-50 text-blue-500'
                      : 'bg-green-50 text-green-500'
                  }`}>
                    {msg.direction === 'inbound'
                      ? <ArrowDownLeft className="h-4 w-4" />
                      : <ArrowUpRight className="h-4 w-4" />
                    }
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={`text-[10px] px-1.5 py-0 ${MODULE_COLORS[msg.module_type] || 'bg-slate-100'}`}>
                        {MODULE_LABELS[msg.module_type] || msg.module_type}
                      </Badge>
                      {msg.entity_reference && (
                        <span className="text-xs font-mono font-semibold text-slate-700">
                          {msg.entity_reference}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(msg.sent_at || msg.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {msg.from_name || msg.from_email}
                      </span>
                      {msg.from_name && msg.from_email && (
                        <span className="text-xs text-slate-400 truncate hidden sm:inline">
                          &lt;{msg.from_email}&gt;
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 font-medium truncate">{msg.subject}</p>
                    <p className="text-xs text-slate-500 truncate mt-0.5 line-clamp-1">
                      {msg.body_plain?.substring(0, 150)}
                    </p>
                  </div>

                  {/* Action */}
                  <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 transition-colors" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between" data-testid="correspondence-pagination">
            <p className="text-sm text-slate-500">
              Page {data.page} of {data.pages} ({data.total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={data.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={data.page >= data.pages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                data-testid="pagination-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
